import { useEffect, useRef } from 'react';

import {
    Container,
    Flex,
    Input,
    Button,
    FormControl,
    FormLabel,
    useBreakpointValue,
    Box,
    Center,
    Text,
    FormErrorMessage,
    useToast,
    Divider
} from '@chakra-ui/react';
import { Formik, Field, Form, FieldProps } from 'formik';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import ReCAPTCHA from 'react-google-recaptcha';

import ForgotPasswordBG from '@/assets/png/NewLoginBG.png';
import PertaminaLogoLogin from '@/assets/PertaminaLogoLogin';
import { COLORS, DURATION_TOAST } from '@/common/constants';
import Footer from '@/components/Layouts/Footer';
import { forgotPasswordSchema } from '@/schemas/forgotPasswordSchema';
import { requestForgotPassword } from '@/services/inspectionUserService';

const siteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY;

type FormContents = {
    email: string,
    captcha: string,
};

const defaultValues: FormContents = {
    email: '',
    captcha: '',
};

const ForgotPassword = () => {
    const isWideScreen = useBreakpointValue({ base: false, md: true });
    const toast = useToast();
    const navigate = useNavigate();

    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const handleSubmit = async (values: FormContents) => {
        try {
            const captchaToken = recaptchaRef.current ? recaptchaRef.current.getValue() : '';

            if (!captchaToken){
                toast({
                    title: 'Please complete the CAPTCHA.',
                    description: '',
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                return;
            }

            const transformedData = {
                email: values.email,
                t: values.captcha,
            }
            
            type ForgotPasswordResponse = { status: number; message?: string };
            const response: ForgotPasswordResponse = await requestForgotPassword(transformedData);
            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'Request sent.',
                    description: "We've sent your request.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Request failed to create.',
                    description: response?.message,
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: 'Request failed to create.',
                description: "We were unable to create the request for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            setTimeout(() => {
                navigate('/');
            }, 2000);
        }
    }

    useEffect(() => {
        Cookies.set('expired_time', 'request_forgot_password');
    }, [])

    return (
        <Container maxW="100%" minH="80vh" p={0}>
            <Flex
                minH="calc(100vh - 70px)"
                direction={isWideScreen ? 'row' : 'column'}
                align="center"
                justify="flex-start"
                bgImage={`url(${ForgotPasswordBG})`}
                bgSize="cover"
                bgPosition="center"
                position="relative"
                overflow="hidden"
            >
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    // bg="rgba(248, 250, 252, 0.9)" // Slightly transparent background for readability
                    p={8}
                    w={isWideScreen ? 500 : '100%'}
                    h={isWideScreen ? '90vh' : 'auto'}
                    borderRadius="md"
                    // boxShadow="md"
                    zIndex="1"
                    alignSelf="flex-start"
                >
                    <Box p={10} borderRadius={10} bg="white" boxShadow='md' style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                        <Center>
                            <PertaminaLogoLogin />
                        </Center>
                        <Text fontSize='30px' mt={6} mb={6} fontWeight='semibold'>Forgot Password?</Text>
                        <Formik
                            initialValues={defaultValues}
                            validationSchema={forgotPasswordSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                        >
                            {(props) => (
                                <Form style={{ marginTop: 0 }}>
                                    <Field name='email'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.email && form.touched.email)} style={{ marginBottom: 15 }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <FormLabel style={{ marginTop: '5px', marginBottom: '15px' }}>
                                                        Please input the registered email, we will send you the reset link to your email
                                                    </FormLabel>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Input {...field} placeholder='Email' style={{ flex: 1, backgroundColor: 'white' }} />
                                                    </div>
                                                </div>
                                                <FormErrorMessage>{String(form.errors.email)}</FormErrorMessage>
                                                {/* <p style={{ marginTop: '10px', textDecoration: 'underline', cursor: 'pointer' }}>Link Idaman</p> */}
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='captcha'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.captcha && form.touched.captcha)} style={{ marginBottom: 15 }}>
                                                {/* @ts-ignore: skip tsc scanning for ReCAPTCHA JSX usage */}
                                                <ReCAPTCHA
                                                    sitekey={siteKey}
                                                    ref={recaptchaRef}
                                                    onChange={(token) => props.setFieldValue('captcha', token ?? '')}
                                                    onErrored={() => props.setFieldValue('captcha', '')}
                                                    onExpired={() => props.setFieldValue('captcha', '')}
                                                />
                                                <FormErrorMessage>{String(form.errors.captcha)}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                    
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={props.isSubmitting}
                                            type='submit'
                                            width='100%'
                                        >
                                            REQUEST RESET PASSWORD
                                        </Button>
                                    </div>
                                    <Flex align="center" my={4}>
                                        <Divider />
                                        <Text px={2} fontSize="sm">
                                            Or
                                        </Text>
                                        <Divider />
                                    </Flex>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outline"
                                            borderColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.BACKGROUND}
                                            bg={COLORS.BUTTON.FOREGROUND}
                                            isLoading={props.isSubmitting}
                                            onClick={() => navigate('/')}
                                            width='100%'
                                        >
                                            BACK TO LOGIN
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Flex>
            </Flex>
            <Footer />
        </Container>
    );
}

export default ForgotPassword;
